import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import PublicShell from '../components/PublicShell'
import { shortHash } from '../components/signals/CallReceipt'
import { callPath } from '../routes'
import { publicCallsAPI } from '../services/api'
import type { CallCheck, DigestListing, FingerprintCheck, FingerprintVerdict } from '../types'
import { formatUtcSeconds } from '../utils/utcTime'
import { recordedSymbol } from '../utils/displaySymbol'

const VERDICTS: Record<FingerprintVerdict, { label: string; tone: string; detail: string }> = {
  match: {
    label: 'Match',
    tone: 'text-green-300',
    detail: 'The stored fields hash to the stored fingerprint, and it is the one the digest lists.',
  },
  fields_changed: {
    label: 'Mismatch',
    tone: 'text-red-300',
    detail: 'The stored fields no longer hash to the fingerprint taken when it was recorded: something was changed since.',
  },
  digest_differs: {
    label: 'Mismatch',
    tone: 'text-red-300',
    detail: 'The stored fingerprint is not the one published in the digest.',
  },
  missing_from_digest: {
    label: 'Mismatch',
    tone: 'text-red-300',
    detail: 'Its digest has been built but does not list it.',
  },
  not_in_digest_yet: {
    label: 'Not in a digest yet',
    tone: 'text-amber-300',
    detail: 'Fingerprinted today; it goes into the digest built after the UTC day closes.',
  },
  not_fingerprinted: {
    label: 'Not fingerprinted',
    tone: 'text-gray-300',
    detail: 'Published before fingerprinting began and not yet backfilled.',
  },
}

const Check = ({ title, check, testId }: { title: string; check: FingerprintCheck; testId: string }) => {
  const verdict = VERDICTS[check.verdict]
  return (
    <div className="rounded-lg border border-terminal-border bg-terminal-bg p-3 text-sm" data-testid={testId} data-verdict={check.verdict}>
      <p className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold text-gray-100">{title}</span>
        <span className={`font-semibold ${verdict.tone}`}>{verdict.label}</span>
      </p>
      <p className="mt-1 text-gray-400">{verdict.detail}</p>
      <dl className="mt-2 space-y-1 font-mono text-xs">
        {(
          [
            ['recomputed now', check.recomputed_hash],
            ['stored', check.stored_hash],
            ['in the digest', check.digest_hash],
          ] as const
        ).map(([label, hash]) => (
          <div key={label} className="flex flex-wrap justify-between gap-2">
            <dt className="text-gray-500">{label}</dt>
            <dd className="text-gray-200" title={hash ?? undefined}>
              {hash ? shortHash(hash) : '—'}
            </dd>
          </div>
        ))}
      </dl>
      {check.fingerprinted_after_publish && check.hashed_at && (
        <p className="mt-2 text-xs text-amber-200" data-testid={`${testId}-backfill`}>
          Fingerprinted {formatUtcSeconds(check.hashed_at)?.slice(0, 10)}; published earlier. This check covers it from that date on.
        </p>
      )}
      {check.digest.url && (
        <p className="mt-2 text-xs">
          <a href={check.digest.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            Its digest file ↗
          </a>
        </p>
      )}
    </div>
  )
}

/**
 * /verify: what the fingerprints are, the daily digest table, and a box that
 * recomputes one call's fingerprints and compares them with the published
 * ones. Public, no login.
 */
const Verify = () => {
  const [params, setParams] = useSearchParams()
  const [listing, setListing] = useState<DigestListing | null>(null)
  const [listingFailed, setListingFailed] = useState(false)
  const [input, setInput] = useState(params.get('id') ?? '')
  const [check, setCheck] = useState<CallCheck | null>(null)
  const [checkState, setCheckState] = useState<'idle' | 'checking' | 'missing' | 'error'>('idle')
  // The checked call's stored symbol, from its public page (so a sealed call
  // stays sealed); null when unknown.
  const [checkedSymbol, setCheckedSymbol] = useState<{ id: number; symbol: string } | null>(null)

  useEffect(() => {
    publicCallsAPI
      .digests()
      .then(setListing)
      .catch(() => setListingFailed(true))
  }, [])

  const run = (raw: string) => {
    const id = raw.trim().replace(/^#/, '')
    if (!/^\d+$/.test(id) || Number(id) < 1) {
      setCheck(null)
      setCheckState('missing')
      return
    }
    setCheckState('checking')
    setCheckedSymbol(null)
    Promise.resolve()
      .then(() => publicCallsAPI.get(id))
      .then((call) => call?.signal && setCheckedSymbol({ id: Number(id), symbol: call.signal.symbol }))
      .catch(() => undefined)
    publicCallsAPI
      .verify(id)
      .then((result) => {
        setCheck(result)
        setCheckState('idle')
      })
      .catch((error) => {
        setCheck(null)
        setCheckState(error?.response?.status === 404 ? 'missing' : 'error')
      })
  }

  useEffect(() => {
    const id = params.get('id')
    if (id) run(id)
    // Only on arrival: later checks are driven by the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setParams(input.trim() ? { id: input.trim().replace(/^#/, '') } : {}, { replace: true })
    run(input)
  }

  const repo = listing?.repo_url ?? 'https://github.com/cryptom321/fincreator-digests'
  const backfillDay = listing?.backfill_day

  return (
    <PublicShell>
      <h1 className="text-2xl font-bold text-gray-50">Verify the record</h1>
      <p className="mt-3 max-w-3xl leading-relaxed text-gray-300" data-testid="verify-explainer">
        Every call the signal engine publishes is fingerprinted the moment it is published: its symbol, direction,
        timeframe, entry, stop, target, mode, publish time and expiry are run through SHA-256, and when it closes, its
        close time, reason, price and result are fingerprinted too. Once a day the fingerprints — and any call excluded
        from the track record that day, with the reason — are committed to a public GitHub repository, where a change
        to an earlier day would show in the repository's history. If a stored call is ever changed, the fingerprint
        recomputed from it stops matching the one published that day. That makes the record tamper-evident, not
        tamper-proof: it cannot stop an edit, but an edit cannot go unnoticed.{' '}
        {backfillDay
          ? `Calls published before ${backfillDay} were fingerprinted on ${backfillDay}, not when they were published, so the check covers them from that date on.`
          : 'Calls published before fingerprinting began will be fingerprinted in one pass, and their receipts will say so: the check covers them from that date on.'}
      </p>
      <p className="mt-3 text-sm">
        <a href={repo} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline" data-testid="repo-link">
          The public digest repository ↗
        </a>
      </p>

      <section className="mt-6 rounded-xl border border-terminal-border bg-terminal-surface p-4 md:p-6" aria-label="Recompute and compare">
        <h2 className="text-lg font-semibold text-gray-100">Recompute and compare</h2>
        <form onSubmit={submit} className="mt-3 flex flex-wrap gap-2">
          <label htmlFor="verify-id" className="sr-only">
            Call id
          </label>
          <input
            id="verify-id"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            inputMode="numeric"
            placeholder="Call id, e.g. 123"
            className="w-48 rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 font-mono text-sm text-gray-100"
          />
          <button type="submit" className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-terminal-bg hover:bg-accent-hover">
            Check
          </button>
        </form>
        {checkState === 'checking' && <p className="mt-3 text-sm text-gray-400">Checking…</p>}
        {checkState === 'missing' && (
          <p className="mt-3 text-sm text-gray-300" data-testid="verify-missing">
            No published call with that id.
          </p>
        )}
        {checkState === 'error' && (
          <p className="mt-3 text-sm text-gray-300" data-testid="verify-error">
            The check could not run. Try again in a minute.
          </p>
        )}
        {check && checkState === 'idle' && (
          <div className="mt-4 space-y-3" data-testid="verify-result">
            <p className="text-sm text-gray-300">
              Call{' '}
              <Link to={callPath(check.id)} className="font-mono text-accent hover:underline">
                #{check.id}
              </Link>
              {checkedSymbol?.id === check.id && (
                <span data-testid="verify-symbol"> · {recordedSymbol(checkedSymbol.symbol)}</span>
              )}
            </p>
            <Check title="The call" check={check.call} testId="check-call" />
            {check.outcome && <Check title="Its outcome" check={check.outcome} testId="check-outcome" />}
          </div>
        )}
        <p className="mt-4 text-xs text-gray-500" data-testid="verify-limit">
          This box is FinCreator checking itself — a convenience, not proof. To check independently, recompute the
          hash from the bytes shown on the call&apos;s page with any SHA-256 tool, or run verify.py from the{' '}
          <a href={repo} target="_blank" rel="noopener noreferrer" className="underline">
            repository
          </a>
          , and find it in that day&apos;s file.
        </p>
      </section>

      <section className="mt-6" aria-label="Daily digests">
        <h2 className="text-lg font-semibold text-gray-100">Daily digests</h2>
        {listingFailed && <p className="mt-2 text-sm text-gray-400">The digest list could not be loaded.</p>}
        {listing && listing.digests.length === 0 && (
          <p className="mt-2 text-sm text-gray-400" data-testid="digests-empty">
            No digest yet: the first is built just after the UTC day closes.
          </p>
        )}
        {listing && listing.digests.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="digest-table">
              <thead>
                <tr className="border-b border-terminal-border text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-4">Day (UTC)</th>
                  <th className="py-2 pr-4 text-right">New calls</th>
                  <th className="py-2 pr-4 text-right">Closed</th>
                  <th className="py-2 pr-4 text-right">Excluded</th>
                  <th className="py-2 pr-4">Digest</th>
                  <th className="py-2">Public</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-terminal-border">
                {listing.digests.map((row) => (
                  <tr key={row.path} data-testid="digest-row">
                    <td className="py-2 pr-4 font-mono text-gray-200">
                      {row.day}
                      {row.kind === 'backfill' && <span className="ml-2 text-xs text-amber-300">backfill</span>}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono">{row.published_count}</td>
                    <td className="py-2 pr-4 text-right font-mono">{row.closed_count}</td>
                    <td className="py-2 pr-4 text-right font-mono">{row.excluded_count}</td>
                    <td className="py-2 pr-4 font-mono text-gray-300" title={row.digest}>
                      {shortHash(row.digest)}
                    </td>
                    <td className="py-2" data-testid="digest-state">
                      {row.state === 'pushed' && row.url ? (
                        <a href={row.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                          Yes ↗
                        </a>
                      ) : row.push_attempts > 0 ? (
                        <span className="text-amber-300">Not yet ({row.push_attempts} failed {row.push_attempts === 1 ? 'try' : 'tries'})</span>
                      ) : (
                        <span className="text-gray-400">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PublicShell>
  )
}

export default Verify
